//#region imports

// react
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

//#endregion

/**
 * Description:
 *    - This component incrementally animates the given numeric value.
 * 
 * Usage:
 *    - <BigLabelValue value={string} label={string} animateCount={boolean} withPlusIcon={boolean} />
 * 
 * Props:
 *    @param {string} value - The numeric value to display (as a string).
 *    @param {string} label - The label displayed below or alongside the value.
 *    @param {boolean} animateCount - Whether to animate the value count-up.
 *    @param {boolean} withPlusIcon - Whether to show a plus icon before the value.
 * 
 * @returns JSX.Element
 */

const BigLabelValue = (props) => {
    const [displayValue, setDisplayValue] = useState(0);
    const targetValue = Number(props.value) || 0;
    // Using intersection observer
    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold: 0.5,  // Animation triggers when 50% of the component is in view
    });

    // side effect runs when component is in-view
    useEffect(() => {
        if (!inView) return;

        let timer;
        const timeout = setTimeout(() => {
            // calculate animation duration based on the target value
            const duration = 2000; // 2 seconds total
            const increment = targetValue / (duration / 20); // increment value

            let current = 0;
            timer = setInterval(() => {
                current += increment;
                if (current >= targetValue) {
                    setDisplayValue(targetValue);
                    clearInterval(timer);
                } else {
                    setDisplayValue(Math.floor(current));
                }
            }, 100);
        }, 100); // wait for animation to start when component in-view

        return () => {
            clearTimeout(timeout);
            clearInterval(timer);
        };
    }, [inView, targetValue]);

    return (
        <div ref={ref} style={{ textAlign: 'center', padding: 22 }}>
            <p style={{ fontSize: 48, fontWeight: 'bold' }}>
                {props?.animateCount
                    ? `${displayValue?.toLocaleString()} ${props?.withPlusIcon ? '+' : ''}`
                    : props?.value}
            </p>
            <p style={{ marginTop: -22 }} className="fw-lighter fs-4">{props?.label}</p>
        </div>
    );
};

export default BigLabelValue;
